// Jalpi WhatsApp configuration — per-branch multi-number.
// A Jalpi "account" is one WhatsApp number (API key + webhook token) assigned to
// one or more branches. Admins manage them here; users send from whichever number
// covers their branch. Backend: /api/jalpi/accounts (routes_jalpi.py).
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, Copy, Check, Trash2, Loader2, Send, Pencil, Info, FileText,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useMasterData } from "@/hooks/use-master-data";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import JalpiTemplates from "@/components/integrations/JalpiTemplates";

const ASSIGNMENT_METHODS = [
  { value: "unassigned", label: "Unassigned (branch pool)" },
  { value: "user", label: "Specific user" },
  { value: "round_robin", label: "Round-robin" },
];

// ── List view ───────────────────────────────────────────────────────────────
function AccountList({ accounts, branches, loading, onAdd, onEdit, onDelete, onTemplates }) {
  const branchName = (id) => branches.find((b) => b.id === id)?.name || id;
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <DialogTitle className="font-display text-navy text-lg">WhatsApp (Jalpi) Numbers</DialogTitle>
        <div className="flex items-center gap-2">
          {/* Templates belong to a number, so they only make sense once one exists. */}
          <Button variant="outline" onClick={onTemplates} disabled={!accounts.length} className="gap-1">
            <FileText className="h-4 w-4" /> Templates
          </Button>
          <Button onClick={onAdd} className="bg-navy text-white hover:bg-navy-hover gap-1">
            <Plus className="h-4 w-4" /> Add Number
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-navy/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 text-muted-foreground">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium w-12">#</th>
              <th className="px-4 py-2.5 font-medium">TITLE</th>
              <th className="px-4 py-2.5 font-medium">NUMBER</th>
              <th className="px-4 py-2.5 font-medium">BRANCHES</th>
              <th className="px-4 py-2.5 font-medium w-20">STATUS</th>
              <th className="px-4 py-2.5 font-medium text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
              </td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                No WhatsApp numbers yet. Click <strong>Add Number</strong> to connect a Jalpi instance.
              </td></tr>
            ) : (
              accounts.map((a, i) => (
                <tr key={a.id} className="border-t border-navy/5">
                  <td className="px-4 py-3">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-navy">
                    {a.title}
                    {a.is_default && (
                      <span className="ml-2 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-semibold text-navy align-middle">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{a.instance_number || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {(a.branch_ids || []).length
                      ? a.branch_ids.map(branchName).filter(Boolean).join(", ")
                      : <span className="text-amber-600">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      a.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                      {a.enabled ? "On" : "Off"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(a)} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700"
                        onClick={() => onDelete(a)} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground flex items-start gap-1.5">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        Messages go from: <strong>1.</strong> the number the person last messaged,{" "}
        <strong>2.</strong> the number linked to their branch, <strong>3.</strong> the{" "}
        <strong>Default</strong> number. Incoming messages arrive on that number's webhook.
      </p>
    </>
  );
}

// ── Form view (add / edit) ───────────────────────────────────────────────────
function AccountForm({ account, branches, telecallers, onBack, onSaved }) {
  const editing = !!account;
  const [title, setTitle] = useState(account?.title || "");
  const [apiKey, setApiKey] = useState(""); // blank = keep stored key
  const [instanceNumber, setInstanceNumber] = useState(account?.instance_number || "");
  const [branchIds, setBranchIds] = useState(account?.branch_ids || []);
  const [enabled, setEnabled] = useState(account?.enabled ?? true);
  const [isDefault, setIsDefault] = useState(account?.is_default ?? false);
  const [autoCreate, setAutoCreate] = useState(account?.auto_create_leads ?? true);
  const [leadAssignment, setLeadAssignment] = useState(account?.lead_assignment || "unassigned");
  const [assignedTo, setAssignedTo] = useState(account?.assigned_to || "");
  const [rrUserIds, setRrUserIds] = useState(account?.rr_user_ids || []);
  const [leadSource, setLeadSource] = useState(account?.lead_source || "WhatsApp");
  const [leadSubSource, setLeadSubSource] = useState(account?.lead_sub_source || "");
  const { sources, subSources } = useMasterData();
  const [webhookUrl, setWebhookUrl] = useState(account?.webhook_url || null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const branchOptions = branches.map((b) => ({ value: b.id, label: b.name }));

  // Keep the stored value selectable even if it's missing from master data
  // (e.g. the legacy hardcoded "WhatsApp" source).
  const sourceOptions = leadSource && !sources.includes(leadSource)
    ? [leadSource, ...sources] : sources;
  const subSourceItems = subSources.filter((s) => s.parent === leadSource);
  const subSourceOptions = leadSubSource && !subSourceItems.some((s) => s.value === leadSubSource)
    ? [{ value: leadSubSource, label: leadSubSource }, ...subSourceItems] : subSourceItems;

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!editing && !apiKey.trim()) { toast.error("Jalpi API key is required"); return; }
    if (autoCreate && leadAssignment === "user" && !assignedTo) {
      toast.error("Pick a user for 'Specific user' assignment"); return;
    }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        instance_number: instanceNumber || null,
        branch_ids: branchIds,
        enabled,
        is_default: isDefault,
        auto_create_leads: autoCreate,
        lead_assignment: leadAssignment,
        assigned_to: leadAssignment === "user" ? assignedTo : null,
        rr_user_ids: leadAssignment === "round_robin" ? rrUserIds : [],
        lead_source: leadSource || "WhatsApp",
        lead_sub_source: leadSubSource || "",
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      };
      const { data } = editing
        ? await api.put(`/jalpi/accounts/${account.id}`, body)
        : await api.post("/jalpi/accounts", { ...body, api_key: apiKey.trim() });
      setWebhookUrl(data.webhook_url);
      setApiKey("");
      if (data.enabled && data.webhook_error)
        toast.warning(`Saved, but webhook registration failed: ${data.webhook_error}`);
      else toast.success(editing ? "Number updated" : "Number added & webhook registered");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const testKey = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/jalpi/accounts/${account?.id || "new"}/test`,
        { api_key: apiKey || null });
      toast.success(`Key valid${data.templates_count != null ? ` — ${data.templates_count} templates` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Key test failed");
    } finally {
      setTesting(false);
    }
  };

  const copyWebhook = async () => {
    if (!webhookUrl) return;
    await navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <DialogTitle className="font-display text-navy text-lg">
          {editing ? "Edit WhatsApp Number" : "Add WhatsApp Number"}
        </DialogTitle>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-navy">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Andheri Clinic WhatsApp" className="mt-1" />
        </div>

        <div>
          <Label className="text-navy">Jalpi API Key {editing && <span className="text-muted-foreground font-normal">(leave blank to keep)</span>}</Label>
          <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder={account?.api_key_masked || "Enter the Jalpi instance API key"}
            className="mt-1 font-mono text-xs" />
        </div>

        <div>
          <Label className="text-navy">Instance Number <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input value={instanceNumber} onChange={(e) => setInstanceNumber(e.target.value)}
            placeholder="WABA number w/ country code, e.g. 919876543210" className="mt-1 font-mono text-xs" />
          <p className="text-[11px] text-muted-foreground mt-1">Used to filter out echoes of your own sends.</p>
        </div>

        <div>
          <Label className="text-navy">Branches served by this number</Label>
          <div className="mt-1">
            <MultiSelect
              options={branchOptions}
              selected={branchIds}
              onChange={setBranchIds}
              placeholder="Select branches…"
              className="w-full"
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Users in these branches send from this number.</p>
        </div>

        <div className="flex items-center gap-2">
          <Switch checked={enabled} onCheckedChange={setEnabled} id="jalpi-enabled" />
          <Label htmlFor="jalpi-enabled" className="text-navy cursor-pointer">Enabled</Label>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Switch checked={isDefault} onCheckedChange={setIsDefault} id="jalpi-default" />
            <Label htmlFor="jalpi-default" className="text-navy cursor-pointer">Default number</Label>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Used when the person has never messaged one of your numbers and no number is linked to their
            branch — for automatic messages, follow-ups, bulk WhatsApp and the chat. Only one number can be
            the default; turning this on removes it from the other number.
            {isDefault && !enabled && (
              <span className="block text-amber-700">This number is switched off, so it won't be used until you enable it.</span>
            )}
          </p>
        </div>

        {/* Auto-create leads from incoming messages */}
        <div className="rounded-lg border border-navy/10 bg-cream/40 p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Switch checked={autoCreate} onCheckedChange={setAutoCreate} id="jalpi-autocreate" />
            <Label htmlFor="jalpi-autocreate" className="text-navy cursor-pointer">Auto-create leads from incoming messages</Label>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1">
            New WhatsApp senders who aren't already leads are added with the source selected below. Existing contacts are matched, not duplicated.
          </p>
          {autoCreate && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Assign new leads to</Label>
                  <Select value={leadAssignment} onValueChange={setLeadAssignment}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {ASSIGNMENT_METHODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {leadAssignment === "user" && (
                  <div className="space-y-1.5">
                    <Label className="text-sm text-navy">User</Label>
                    <SearchableSelect
                      options={telecallers.map((u) => ({ value: u.id, label: u.name }))}
                      value={assignedTo}
                      onChange={setAssignedTo}
                      placeholder="Select user"
                      searchPlaceholder="Search users…"
                    />
                  </div>
                )}
                {leadAssignment === "round_robin" && (
                  <div className="space-y-1.5">
                    <Label className="text-sm text-navy">
                      Users in rotation <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <MultiSelect
                      options={telecallers.map((u) => ({ value: u.id, label: u.name }))}
                      selected={rrUserIds}
                      onChange={setRrUserIds}
                      placeholder="All branch telecallers"
                      className="w-full"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {rrUserIds.length > 0
                        ? `New leads rotate across these ${rrUserIds.length} user${rrUserIds.length > 1 ? "s" : ""}, one each in turn.`
                        : "Leave empty to rotate across every active telecaller in the selected branches."}
                    </p>
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Source</Label>
                  <SearchableSelect
                    options={sourceOptions.map((s) => ({ value: s, label: s }))}
                    value={leadSource}
                    onChange={(v) => { setLeadSource(v); setLeadSubSource(""); }}
                    placeholder="Select source"
                    searchPlaceholder="Search sources…"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-navy">Sub Source</Label>
                  <SearchableSelect
                    options={subSourceOptions}
                    value={leadSubSource}
                    onChange={setLeadSubSource}
                    placeholder="Select sub source"
                    searchPlaceholder="Search sub sources…"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div>
          <Button variant="outline" onClick={testKey} disabled={testing} className="gap-1.5">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {testing ? "Testing…" : "Test key"}
          </Button>
        </div>

        {webhookUrl && (
          <div>
            <Label className="text-navy">Webhook URL</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input readOnly value={webhookUrl} className="font-mono text-xs bg-cream/50" />
              <Button variant="outline" size="icon" onClick={copyWebhook} title="Copy">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Registered with Jalpi automatically. Incoming messages arrive here.</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-navy/10">
          <Button variant="ghost" onClick={onBack}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover gap-1.5">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? "Save changes" : "Add number"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Modal shell ──────────────────────────────────────────────────────────────
export default function JalpiConfigModal({ open, onOpenChange }) {
  const [view, setView] = useState("list");   // list | form | templates
  const [editing, setEditing] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [telecallers, setTelecallers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, brRes, tcRes] = await Promise.all([
        api.get("/jalpi/accounts"),
        api.get("/branches"),
        api.get("/users", { params: { role: "telecaller" } }).catch(() => ({ data: [] })),
      ]);
      setAccounts(accRes.data || []);
      setBranches(brRes.data || []);
      setTelecallers(tcRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setView("list");
    setEditing(null);
    load();
  }, [open, load]);

  const remove = async (a) => {
    if (!window.confirm(`Remove WhatsApp number "${a.title}"?`)) return;
    try {
      await api.delete(`/jalpi/accounts/${a.id}`);
      toast.success("Number removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="sr-only"><DialogTitle>WhatsApp (Jalpi)</DialogTitle></DialogHeader>
        {view === "templates" ? (
          <JalpiTemplates accounts={accounts} onBack={() => setView("list")} />
        ) : view === "list" ? (
          <AccountList
            accounts={accounts}
            branches={branches}
            loading={loading}
            onAdd={() => { setEditing(null); setView("form"); }}
            onEdit={(a) => { setEditing(a); setView("form"); }}
            onDelete={remove}
            onTemplates={() => setView("templates")}
          />
        ) : (
          <AccountForm
            account={editing}
            branches={branches}
            telecallers={telecallers}
            onBack={() => { setView("list"); load(); }}
            onSaved={() => { /* stay on form to show webhook URL; list refreshes on back */ }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
